import { useId } from 'react';
import { Link } from 'react-router-dom';
import type { QuestionSearchCard as Card } from '../../domain/entities/content.entity';
import { FlagBadges, type QuestionFlag } from './QuestionFlagBadges';

/** "USP · SP · 2024 · Caderno 01", sem as partes que a prova não tem. */
export function examLabel(exam: NonNullable<Card['exam']>): string {
  return [
    exam.institution?.initials,
    exam.institution?.uf,
    String(exam.year),
    exam.variant,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** O nome do link: o número e a prova, que é como a pessoa acha a questão. */
export function questionCardName(card: Card): string {
  if (!card.exam) return 'Questão sem prova';
  const number =
    card.exam.position !== null ? `Questão ${card.exam.position}` : 'Questão';
  return `${number}, ${examLabel(card.exam)}`;
}

function cardFlags(card: Card): QuestionFlag[] {
  return [
    ...(card.annulled ? (['annulled'] as const) : []),
    ...(card.appealable ? (['appealable'] as const) : []),
    ...(card.outdated ? (['outdated'] as const) : []),
  ];
}

/**
 * Uma linha da busca de questões (DESEN-42), e a mesma da Gestão de
 * comentários (DESEN-43). O card inteiro é um link para a ficha da questão,
 * e leva no `state` de onde veio, para o "Voltar" da ficha devolver a busca.
 *
 * A contagem de reports vem por props porque cada tela conta uma coisa: aqui,
 * os reports da questão; na Gestão de comentários, os do comentário. Com 0,
 * ela some.
 */
export function QuestionSearchCard({
  card,
  reportCount,
  reportLabel,
  from,
}: {
  card: Card;
  reportCount: number;
  /** O que vem depois de "N report(s)", ex.: "na questão". */
  reportLabel: string;
  /** A busca de onde se veio (`pathname + search`). */
  from: string;
}) {
  const id = useId();
  const flagsId = `${id}-flags`;
  const excerptId = `${id}-excerpt`;
  const metaId = `${id}-meta`;
  const flags = cardFlags(card);
  const path = card.subjectPath.map((s) => s.name).join(' > ');

  return (
    <Link
      to={`/content/items/${card.questionId}`}
      state={{ from }}
      aria-label={questionCardName(card)}
      // O nome é o número e a prova; as badges, o trecho, a classificação e
      // quem edita vão na descrição, para o leitor de tela também saber que a
      // questão é anulada ou tem report.
      aria-describedby={`${flagsId} ${excerptId} ${metaId}`}
      className="border-(--color-border) bg-(--color-bg-primary) hover:bg-(--color-bg-secondary) focus-visible:ring-(--color-accent) block rounded-lg border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm">
          {card.exam ? (
            <>
              {card.exam.position !== null && (
                <span className="text-(--color-text-primary) font-medium">
                  Questão {card.exam.position}
                </span>
              )}
              <span className="text-(--color-text-secondary) ml-2">
                {examLabel(card.exam)}
              </span>
            </>
          ) : (
            <span className="text-(--color-text-secondary)">Sem prova</span>
          )}
        </p>
        <div id={flagsId} className="flex flex-wrap items-center gap-1.5">
          <FlagBadges flags={flags} />
          {reportCount > 0 && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-500/15 dark:text-red-400">
              {reportCount} {reportCount === 1 ? 'report' : 'reports'}{' '}
              {reportLabel}
            </span>
          )}
        </div>
      </div>

      <p
        id={excerptId}
        className="text-(--color-text-primary) mt-2 line-clamp-2 text-sm"
      >
        {card.excerpt}
      </p>

      <div
        id={metaId}
        className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs"
      >
        <p className="text-(--color-text-secondary)">
          {path || 'Sem classificação'}
        </p>
        {card.editingBy && (
          <p className="text-(--color-text-secondary) italic">
            {card.editingBy.name
              ? `Sendo editada agora por ${card.editingBy.name}`
              : 'Sendo editada agora'}
          </p>
        )}
      </div>
    </Link>
  );
}

/**
 * O esqueleto do card, com a altura dele, para a página não pular quando a
 * primeira resposta chega. A lista é de cards, e não de `Table`: o
 * `SkeletonRow` do design system não serve.
 */
export function QuestionSearchCardSkeleton() {
  return (
    <div
      aria-hidden
      className="border-(--color-border) animate-pulse rounded-lg border p-4"
    >
      <div className="bg-(--color-bg-secondary) h-5 w-1/3 rounded" />
      {/* As duas linhas do trecho (`line-clamp-2`, 40px), como no card. */}
      <div className="bg-(--color-bg-secondary) mt-2 h-10 w-full rounded" />
      <div className="bg-(--color-bg-secondary) mt-2 h-4 w-1/2 rounded" />
    </div>
  );
}
